import { Button } from '@zoopla-eng/zds/button';
import { SearchPageType, SearchSource } from '@zoopla/listing-search-context';
import {
    FormValues,
    getDuration,
    getFormValuesFromQuery,
    getQ,
    getSearchPath,
    getTransportType
} from '@zoopla/listing-search/filters/modal';
import { LocationSearchInput } from '@zoopla/listing-search/input';
import { triggerTracking } from '@zoopla/tracking';
import { NextRouter, useRouter } from 'next/router';
import { FC, useEffect, useState } from 'react';
import { formatLocation } from '../utils/formatLocation';
import { formatSearchPath } from '../utils/formatSearchPath';
import { getQueryParams } from '../utils/getQueryParams';
import {
    bedroomsWrapperStyle,
    locationWrapperStyle,
    priceRangeMultiColumnDropdownWrapperStyle,
    propertyTypeWrapperStyle,
    searchInitationWrapperStyle,
    searchRadiusWrapperStyle,
    styledButtonWrapperStyle,
    styledDividerInnerStyle,
    styledDividerStyle,
    travelTimeWrapperStyle
} from './SearchInitiation.styles.css';
import { BedsMax, BedsMin, SelectBeds } from './SelectBeds';
import {
    Duration,
    SelectMaxTravelTimeAndTransportMode,
    TransportType
} from './SelectMaxTravelTimeAndTransportMode';
import { PriceMax, PriceMin, SelectPriceForSale } from './SelectPriceForSale';
import { PriceFrequency, SelectPriceToRent } from './SelectPriceToRent';
import {
    PropertySubType,
    PropertyType,
    SelectPropertyType
} from './SelectPropertyType';
import { Radius, SelectRadius } from './SelectRadius';

type Tab = SearchPageType | 'house-prices';

const SEARCH_BUTTON_TEXT = 'Search';

export type SearchInitiationProps = {
    searchType: Tab;
    showRadius?: boolean;
    eventCategory: 'homepage' | 'search results' | 'new_homes';
    searchSource: SearchSource;
};

const getIdentifier = (query: NextRouter['query']) =>
    formatLocation(getSearchPath(query));
const getLocation = (query: NextRouter['query']) =>
    getQ(query) || formatSearchPath(getSearchPath(query));

export const SearchInitiation: FC<
    React.PropsWithChildren<SearchInitiationProps>
> = ({ searchType, showRadius = true, eventCategory, searchSource }) => {
    const { query, push, pathname, isReady } = useRouter();
    const [resolvedQuery, updateResolvedQuery] = useState(query);
    const [formValue, updateFormValue] = useState<FormValues>(
        getFormValuesFromQuery(query)
    );
    const [locationText, setLocationText] = useState(getLocation(query));
    const [identifier, setIdentifier] = useState(getIdentifier(query));
    const [hasSubmittedLocationSearch, setHasSubmittedLocationSearch] =
        useState(false);

    useEffect(() => {
        if (isReady) {
            updateResolvedQuery(query);
            updateFormValue((o) => ({
                ...o,
                ...getFormValuesFromQuery(query)
            }));
            setLocationText(getLocation(query));
        }
    }, [query, isReady]);

    const isTravelTimeSearch = Boolean(
        getDuration(resolvedQuery) && getTransportType(resolvedQuery)
    );
    const isForSaleSearch =
        searchType === 'for-sale' || searchType === 'new-homes';
    const isNewHomesDevelopersSearch = searchSource === 'new-homes-dev';
    const viewType = pathname.includes('/map/') ? 'map' : 'list';

    const handleSearchProperties = async () => {
        if (!locationText) {
            setHasSubmittedLocationSearch(true);
            return;
        }

        const eventLabel = locationText
            ? `${searchType}-success`
            : `${searchType}-empty-search`;

        triggerTracking({
            event: 'GAEvent',
            eventCategory,
            eventAction: 'search',
            eventLabel
        });

        if (isNewHomesDevelopersSearch) {
            await push({
                pathname: identifier
                    ? `/new-homes/developers/${identifier}/`
                    : `/new-homes/developers/`,
                query: {
                    ...(formValue.beds_max && { beds_max: formValue.beds_max }),
                    ...(formValue.beds_min && { beds_min: formValue.beds_min }),
                    ...(formValue.price_min && {
                        price_min: formValue.price_min
                    }),
                    ...(formValue.price_max && {
                        price_max: formValue.price_max
                    }),
                    property_type: formValue.property_type,
                    q: formValue.q || locationText
                }
            });

            return;
        }

        const queryParams = getQueryParams(searchType);

        await push({
            pathname: '/search/',
            query: {
                ...queryParams,
                ...(formValue.beds_max && { beds_max: formValue.beds_max }),
                ...(formValue.beds_min && { beds_min: formValue.beds_min }),
                ...(formValue.price_min && {
                    price_min: formValue.price_min
                }),
                ...(formValue.price_max && {
                    price_max: formValue.price_max
                }),
                ...(formValue.property_sub_type && {
                    property_sub_type: formValue.property_sub_type
                }),
                ...(formValue.radius && { radius: formValue.radius }),
                ...(formValue.duration && { duration: formValue.duration }),
                ...(formValue.transport_type && {
                    transport_type: formValue.transport_type
                }),
                property_type: formValue.property_type,
                q: formValue.q || locationText,
                view_type: viewType
            }
        });
    };

    const renderRadiusAndTravelTime = () => {
        if (!isTravelTimeSearch) {
            if (showRadius) {
                return (
                    <>
                        <div className={styledDividerStyle}>
                            <div className={styledDividerInnerStyle} />
                        </div>
                        <div className={searchRadiusWrapperStyle}>
                            <SelectRadius
                                value={formValue.radius as Radius}
                                onChange={(radius) => {
                                    updateFormValue((oldFormValue) => ({
                                        ...oldFormValue,
                                        radius: radius
                                    }));
                                }}
                            />
                        </div>
                    </>
                );
            }

            return null;
        }

        return (
            <div className={travelTimeWrapperStyle}>
                <SelectMaxTravelTimeAndTransportMode
                    value={{
                        duration: formValue.duration as Duration,
                        transportType: formValue.transport_type as TransportType
                    }}
                    onChange={({ duration, transportType }) => {
                        updateFormValue((oldFormValue) => ({
                            ...oldFormValue,
                            duration: duration,
                            transport_type: transportType
                        }));
                    }}
                />
            </div>
        );
    };

    const showError = hasSubmittedLocationSearch && !locationText;

    return (
        <div className={searchInitationWrapperStyle}>
            <div className={locationWrapperStyle}>
                <LocationSearchInput
                    id="header-location"
                    onLocationInputKeyDown={handleSearchProperties}
                    hasExtendedHeight
                    hasDarkPurpleBorder
                    hasBorderRight={false}
                    value={decodeURIComponent(locationText as string)}
                    showError={showError}
                    onlyDisplayRemoveIconWhenActive={false}
                    onLocationSelect={({ identifier, q }) => {
                        updateFormValue((oldFormValue) => ({
                            ...oldFormValue,
                            q: q
                        }));
                        setIdentifier(identifier);
                        setLocationText(q);
                    }}
                    handleInputTextChange={(value) => {
                        updateFormValue((oldFormValue) => ({
                            ...oldFormValue,
                            q: ''
                        }));
                        setIdentifier(value);
                        setLocationText(value);
                    }}
                    onClearText={() => setLocationText('')}
                />
            </div>
            {renderRadiusAndTravelTime()}

            <div className={styledDividerStyle}>
                <div className={styledDividerInnerStyle} />
            </div>

            <div className={bedroomsWrapperStyle}>
                <SelectBeds
                    value={{
                        bedsMin: formValue.beds_min as BedsMin,
                        bedsMax: formValue.beds_max as BedsMax
                    }}
                    onChange={({ bedsMin, bedsMax }) => {
                        updateFormValue((oldFormValue) => ({
                            ...oldFormValue,
                            beds_min: bedsMin,
                            beds_max: bedsMax
                        }));
                    }}
                />
            </div>

            <div className={styledDividerStyle}>
                <div className={styledDividerInnerStyle} />
            </div>

            <div className={priceRangeMultiColumnDropdownWrapperStyle}>
                {isForSaleSearch ? (
                    <SelectPriceForSale
                        value={{
                            priceMin: formValue.price_min as PriceMin,
                            priceMax: formValue.price_max as PriceMax
                        }}
                        onChange={({ priceMin, priceMax }) => {
                            updateFormValue((oldFormValue) => ({
                                ...oldFormValue,
                                price_min: priceMin,
                                price_max: priceMax
                            }));
                        }}
                    />
                ) : (
                    <SelectPriceToRent
                        value={{
                            priceMin: formValue.price_min as PriceMin,
                            priceMax: formValue.price_max as PriceMax,
                            priceFrequency: (formValue.price_frequency ||
                                'per_month') as PriceFrequency
                        }}
                        onChange={({ priceMin, priceMax, priceFrequency }) => {
                            updateFormValue((oldFormValue) => ({
                                ...oldFormValue,
                                price_min: priceMin,
                                price_max: priceMax,
                                price_frequency: priceFrequency
                            }));
                        }}
                    />
                )}
            </div>

            <div className={styledDividerStyle}>
                <div className={styledDividerInnerStyle} />
            </div>

            <div className={propertyTypeWrapperStyle}>
                <SelectPropertyType
                    value={{
                        propertyType: formValue.property_type as PropertyType,
                        propertySubType:
                            formValue.property_sub_type as PropertySubType
                    }}
                    onChange={({ propertySubType, propertyType }) => {
                        updateFormValue((oldFormValue) => ({
                            ...oldFormValue,
                            property_sub_type: propertySubType,
                            property_type: propertyType
                        }));
                    }}
                />
            </div>

            <div className={styledButtonWrapperStyle}>
                <Button
                    category="primary"
                    onClick={handleSearchProperties}
                    data-testid="search-button"
                >
                    {SEARCH_BUTTON_TEXT}
                </Button>
            </div>
        </div>
    );
};
